import locale from "@bonliva-crm/locale";
import { ErrorCode } from "@bonliva-crm/common";

// Thunks, not plain strings: locale is a Proxy, so the lookups must not run at
// module-evaluation time.
const messagesByCode: Partial<Record<ErrorCode, () => string>> = {
  [ErrorCode.TimereportAlreadySentToCustomer]: () =>
    locale.timereportSendingToCustomer.alreadySentToCustomer,
  [ErrorCode.TimereportNotSendableStatus]: () =>
    locale.timereportSendingToCustomer.notSendableStatus,
  [ErrorCode.TimereportNoCustomerContact]: () =>
    locale.timereportSendingToCustomer.noCustomerContact,
  [ErrorCode.TimereportEmailSendFailed]: () =>
    locale.timereportSendingToCustomer.emailSendFailed,
  [ErrorCode.TimereportEmailPartiallySent]: () =>
    locale.timereportSendingToCustomer.emailPartiallySent
};

/**
 * Reads the error code the API puts in `extensions.code` off an ApolloError-shaped
 * value, without depending on @apollo/client here — the store catches whatever the
 * mutation rejects with, which may also be a plain Error.
 */
function extractCode(error: unknown): ErrorCode | null {
  const graphQLErrors = (
    error as { graphQLErrors?: { extensions?: { code?: string } }[] }
  )?.graphQLErrors;

  const code = graphQLErrors?.[0]?.extensions?.code;

  return code ? (code as ErrorCode) : null;
}

/**
 * Translates an error from the send-timereport-to-customer mutation to a Swedish
 * string, keyed off the backend error code. Anything without a known code falls
 * back to a generic Swedish string, so the raw English backend message never
 * reaches the user.
 *
 * The fallback says the timereport was not sent rather than "Okänt fel": the
 * mutation only rejects on paths where the customer got nothing, or where only
 * some recipients did, and the latter has its own code (CRMDEV-7141).
 */
export function translateSendTimereportError(error: unknown): string {
  const code = extractCode(error);
  const message = code ? messagesByCode[code] : undefined;

  return message?.() ?? locale.timereportSendingToCustomer.sendFailedUnknown;
}
